import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { StripeAPIService } from '../../services/stripe-api.service';
import { AmplifyModelService } from '../../services/amplify-model-service.service';

@Component({
    selector: 'app-stripe-redirect-page',
    imports: [],
    templateUrl: './stripe-redirect-page.component.html',
    styleUrl: './stripe-redirect-page.component.css'
})
export class StripeRedirectPageComponent implements OnInit, OnDestroy {

  sessionId: string = '';
  subscriptionId: string = '';
  customerId: string = '';
  state: string = '';
  countdown: number = 5;
  private subscription!: Subscription;
  private stripeApi = inject(StripeAPIService);
  private ams = inject(AmplifyModelService);

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) { }

  async ngOnInit() {
    this.route.paramMap.subscribe(async (params) => {
      this.sessionId = params.get('sessionId') || '';
      this.customerId = params.get('customerId') || '';
      this.state = params.get('state') || '';

      console.log('StripeRedirect - sessionId:', this.sessionId, 'customerId:', this.customerId, 'state:', this.state);

      // If this is a successful subscription creation, update the Sub record
      if (this.state === 'success' && this.sessionId) {
        await this.handleSuccessfulSubscription();
      }

      this.startCountdown();
    });
  }

  private async handleSuccessfulSubscription(): Promise<void> {
    try {
      console.log('🔄 Handling successful subscription for session:', this.sessionId);
      
      // Get the Stripe session details. The backend's getSession call expands
      // 'subscription', so session.subscription is already a full Subscription
      // object (not just an id) - use it directly rather than making a second
      // getSubscription() call, which would incorrectly pass that object where
      // Stripe's subscriptions.retrieve() expects a plain subscription id string.
      const session = await this.stripeApi.getSession(this.sessionId) as any;
      
      if (session && session.subscription) {
        console.log('✅ Retrieved Stripe session:', session);
        
        const subscription = session.subscription;
        
        if (subscription && subscription.metadata && subscription.metadata.appId) {
          const subId = subscription.metadata.appId;
          console.log('🔗 Updating Sub record:', subId, 'with Stripe ID:', subscription.id);
          
          // Update the Sub record with the Stripe subscription ID and initial pricing data
          const updateData: any = {
            id: subId,
            stripe_id: subscription.id,
            stripe_type: subscription.object,
            stripe_status: this.mapStripeStatusToEnum(subscription.status)
          };

          // Extract pricing data from Stripe subscription
          if (subscription.items && subscription.items.data && subscription.items.data.length > 0) {
            const totalAmount = subscription.items.data.reduce((total: number, item: any) => {
              return total + (item.price.unit_amount || 0);
            }, 0);
            
            updateData.subscription_amount = totalAmount / 100; // Convert from cents to dollars
            
            // Look for initial service fee (one-time items)
            const initialFeeItem = subscription.items.data.find((item: any) => 
              item.price.type === 'one_time' || item.price.recurring === null
            );
            
            if (initialFeeItem) {
              updateData.initial_service_fee = (initialFeeItem.price.unit_amount || 0) / 100;
            }
          }

          // Set billing dates
          if (subscription.current_period_end) {
            updateData.next_payment_date = new Date(subscription.current_period_end * 1000).toISOString().split('T')[0];
          }

          const result = await this.ams.updateItemForModel('Sub', updateData);
          
          if (result.data) {
            console.log('✅ Successfully updated Sub record with Stripe data:', result.data);
          } else {
            console.error('❌ Failed to update Sub record:', result.errors);
          }
        } else {
          console.warn('⚠️ No appId found in subscription metadata');
        }
      } else {
        console.warn('⚠️ No subscription found in session');
      }
    } catch (error) {
      console.error('❌ Error handling successful subscription:', error);
    }
  }

  private mapStripeStatusToEnum(stripeStatus: string): 'ACTIVE' | 'PAST_DUE' | 'UNPAID' | 'CANCELED' | 'INCOMPLETE' | 'INCOMPLETE_EXPIRED' | 'TRIALING' | 'PAUSED' | null {
    const statusMap: { [key: string]: 'ACTIVE' | 'PAST_DUE' | 'UNPAID' | 'CANCELED' | 'INCOMPLETE' | 'INCOMPLETE_EXPIRED' | 'TRIALING' | 'PAUSED' } = {
      'active': 'ACTIVE',
      'past_due': 'PAST_DUE',
      'unpaid': 'UNPAID',
      'canceled': 'CANCELED',
      'cancelled': 'CANCELED',
      'incomplete': 'INCOMPLETE',
      'incomplete_expired': 'INCOMPLETE_EXPIRED',
      'trialing': 'TRIALING',
      'paused': 'PAUSED'
    };
    
    return statusMap[stripeStatus.toLowerCase()] || null;
  }

  private startCountdown(): void {
    this.subscription = interval(1000).subscribe(() => {
      this.countdown--;
      if (this.countdown < 0) {
        this.subscription.unsubscribe();
        this.router.navigate(['/customers', this.customerId]);
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
